import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, DollarSign, FileText, CreditCard, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  useProjectWithBilling,
  useUpdateBillingStatus,
  useRestoreProject,
  useProjectBillingHistory,
} from '@/hooks/api/useProjects';
import { BillingStatus } from '@/api/projects';
import Routes from '@/router/routes';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { formatDate, formatDateTime } from '@/lib/date';

interface UpdateBillingFormState {
  billingStatus: BillingStatus | '';
  notes: string;
  billingAmount: string;
  billingCurrency: string;
  billingReference: string;
}

const BILLING_STATUS_LABELS: Record<BillingStatus, string> = {
  [BillingStatus.QUOTED]: 'Quoted',
  [BillingStatus.INVOICED]: 'Invoiced',
  [BillingStatus.PAID]: 'Paid',
};

export default function SuperAdminSingleProject() {
  const { project_id } = useParams<{ project_id: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('overview');
  const [updateBillingDialogOpen, setUpdateBillingDialogOpen] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [billingForm, setBillingForm] = useState<UpdateBillingFormState>({
    billingStatus: '',
    notes: '',
    billingAmount: '',
    billingCurrency: 'EUR',
    billingReference: '',
  });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant?: 'default' | 'destructive';
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  // Stable filter reference — inline objects cause infinite re-renders
  // because React Query treats each new object reference as a key change.
  // Memoized on historyPage so pagination still triggers a refetch when
  // the user clicks through billing history. Matches the pattern applied
  // in SuperAdminSingleWorkspace by #1423.
  const billingHistoryFilters = useMemo(
    () => ({ page: historyPage, limit: 10 }),
    [historyPage],
  );

  const { data: project, isLoading, isError, refetch } = useProjectWithBilling(project_id || '');
  const { data: billingHistory, isLoading: historyLoading } = useProjectBillingHistory(
    project_id || '',
    billingHistoryFilters,
  );
  const updateBillingStatus = useUpdateBillingStatus();
  const restoreProject = useRestoreProject();

  const handleBack = () => {
    navigate(Routes.super_admin_dashboard_projects.path);
  };

  const handleRestore = () => {
    if (!project_id || !project) return;
    setConfirmDialog({
      open: true,
      title: 'Restore Project',
      description: `Are you sure you want to restore "${project.title}"? The project will be accessible again.`,
      onConfirm: () => {
        restoreProject.mutate(
          {
            id: project_id,
            organizationId: project.organizationId,
            workspaceId: project.workspaceId,
          },
          { onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })) }
        );
      },
    });
  };

  const handleOpenUpdateBilling = () => {
    setBillingForm({
      billingStatus: project?.billingStatus || '',
      notes: project?.billingNotes || '',
      billingAmount: '',
      billingCurrency: 'EUR',
      billingReference: '',
    });
    setUpdateBillingDialogOpen(true);
  };

  const handleSubmitBilling = () => {
    if (!project_id || !billingForm.billingStatus) return;
    const payload: any = { billingStatus: billingForm.billingStatus };
    if (billingForm.notes.trim()) payload.notes = billingForm.notes.trim();
    if (billingForm.billingAmount.trim()) {
      payload.billingAmount = parseFloat(billingForm.billingAmount);
    }
    if (billingForm.billingCurrency.trim()) payload.billingCurrency = billingForm.billingCurrency.trim();
    if (billingForm.billingReference.trim()) payload.billingReference = billingForm.billingReference.trim();

    updateBillingStatus.mutate(
      { id: project_id, payload },
      {
        onSuccess: () => {
          setUpdateBillingDialogOpen(false);
        },
      }
    );
  };

  const getBillingBadgeVariant = (status?: BillingStatus): 'default' | 'warning' | 'success' | 'info' => {
    switch (status) {
      case BillingStatus.QUOTED:
        return 'warning';
      case BillingStatus.INVOICED:
        return 'info';
      case BillingStatus.PAID:
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusBadgeVariant = (status?: string) => {
    if (!status) return 'default';
    switch (status.toLowerCase()) {
      case 'active':
      case 'completed':
        return 'success';
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount?: number, currency?: string) => {
    if (amount === undefined || amount === null) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-red-600 mb-4">Failed to load data. Please try again.</p>
        <Button variant="outline" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col gap-6">
        <Button variant="ghost" onClick={handleBack} className="w-fit">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Button>
        <div className="bg-card rounded-lg shadow-sm border border-border p-8 text-center">
          <p className="text-red-600">Project not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs items={[
        { label: 'Projects', path: Routes.super_admin_dashboard_projects.path },
        { label: project.title },
      ]} />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={handleBack} size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-foreground">{project.title}</h1>
              <Badge variant={getStatusBadgeVariant(project.status) as any}>
                {project.status}
              </Badge>
              {project.deletedAt && <Badge variant="deleted">Deleted</Badge>}
            </div>
            <p className="text-muted-foreground mt-1">
              <Link
                to={`${Routes.super_admin_dashboard_organizations.path}/${project.organizationId}`}
                className="hover:underline text-blue-600"
              >
                {project.organizationName}
              </Link>
              {' / '}
              <Link
                to={`${Routes.super_admin_dashboard_workspaces.path}/${project.workspaceId}`}
                className="hover:underline text-blue-600"
              >
                {project.workspaceName}
              </Link>
            </p>
          </div>
        </div>
        {project.deletedAt && (
          <Button onClick={handleRestore} disabled={restoreProject.isPending}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Restore Project
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Project Stats</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant={getStatusBadgeVariant(project.status) as any}>
                    {project.status}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Respondents</span>
                  <span className="font-semibold">
                    {project.respondentsCount || 0} / {project.respondentsMax || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Conversations</span>
                  <span className="font-semibold">
                    {project.conversationsCompleted || 0} / {project.conversationsTotal || 0}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Project Details</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-muted-foreground text-sm">Created</span>
                  <p className="font-medium">{formatDateTime(project.createdAt)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Organization</span>
                  <p className="font-medium">
                    <Link
                      to={`${Routes.super_admin_dashboard_organizations.path}/${project.organizationId}`}
                      className="text-blue-600 hover:underline"
                    >
                      {project.organizationName}
                    </Link>
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Workspace</span>
                  <p className="font-medium">
                    <Link
                      to={`${Routes.super_admin_dashboard_workspaces.path}/${project.workspaceId}`}
                      className="text-blue-600 hover:underline"
                    >
                      {project.workspaceName}
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">IDs</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-muted-foreground text-sm">Project ID</span>
                  <p className="font-mono text-sm bg-muted p-2 rounded mt-1 break-all">
                    {project._id}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Workspace ID</span>
                  <p className="font-mono text-sm bg-muted p-2 rounded mt-1 break-all">
                    {project.workspaceId}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Organization ID</span>
                  <p className="font-mono text-sm bg-muted p-2 rounded mt-1 break-all">
                    {project.organizationId}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="mt-6">
          <div className="flex flex-col gap-6">
            {/* Current Billing Status Card */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Current Billing Status</h3>
                <Button onClick={handleOpenUpdateBilling} size="sm">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Update Billing
                </Button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <span className="text-muted-foreground text-sm">Status</span>
                  <div className="mt-1">
                    <Badge variant={getBillingBadgeVariant(project.billingStatus)}>
                      {project.billingStatus ? BILLING_STATUS_LABELS[project.billingStatus] : 'Not Set'}
                    </Badge>
                  </div>
                </div>

                {project.billingStatusUpdatedAt && (
                  <div>
                    <span className="text-muted-foreground text-sm">Last Updated</span>
                    <p className="font-medium mt-1">{formatDateTime(project.billingStatusUpdatedAt)}</p>
                  </div>
                )}

                {project.billingStatusUpdatedBy?.email && (
                  <div>
                    <span className="text-muted-foreground text-sm">Updated By</span>
                    <p className="font-medium mt-1">
                      {project.billingStatusUpdatedBy.name || project.billingStatusUpdatedBy.email}
                    </p>
                  </div>
                )}

                {project.billingAmount !== undefined && project.billingAmount !== null && (
                  <div>
                    <span className="text-muted-foreground text-sm">Amount</span>
                    <p className="font-medium mt-1">
                      {formatCurrency(project.billingAmount, project.billingCurrency)}
                    </p>
                  </div>
                )}

                {project.billingReference && (
                  <div>
                    <span className="text-muted-foreground text-sm">Reference</span>
                    <p className="font-medium mt-1 font-mono text-sm">{project.billingReference}</p>
                  </div>
                )}

                {project.billingNotes && (
                  <div className="sm:col-span-2 lg:col-span-3">
                    <span className="text-muted-foreground text-sm">Notes</span>
                    <p className="font-medium mt-1 text-sm">{project.billingNotes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Billing History Timeline */}
            <div className="bg-card rounded-lg shadow-sm border border-border">
              <div className="p-4 border-b border-border">
                <h3 className="text-lg font-semibold">Billing History</h3>
              </div>

              {historyLoading ? (
                <div className="p-4 space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : !billingHistory || billingHistory.data.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-muted-foreground">No billing history yet.</p>
                </div>
              ) : (
                <div className="p-6">
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-border" />

                    <div className="space-y-6">
                      {billingHistory.data.map((entry) => (
                        <div key={entry._id} className="relative pl-12">
                          {/* Timeline dot */}
                          <div className="absolute left-2.5 top-1.5 h-3 w-3 rounded-full border-2 border-card bg-muted-foreground ring-2 ring-border" />

                          <div className="bg-muted rounded-lg p-4 border border-border">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <Badge variant={getBillingBadgeVariant(entry.billingStatus)}>
                                {BILLING_STATUS_LABELS[entry.billingStatus]}
                              </Badge>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatDate(entry.updatedAt)}
                              </span>
                            </div>

                            <div className="space-y-1">
                              {entry.updatedBy && (
                                <p className="text-sm text-muted-foreground">
                                  By {entry.updatedBy.name || entry.updatedBy.email}
                                </p>
                              )}
                              {entry.billingAmount !== undefined && entry.billingAmount !== null && (
                                <p className="text-sm text-muted-foreground">
                                  Amount: {formatCurrency(entry.billingAmount, entry.billingCurrency)}
                                </p>
                              )}
                              {entry.billingReference && (
                                <p className="text-sm text-muted-foreground font-mono">
                                  Ref: {entry.billingReference}
                                </p>
                              )}
                              {entry.notes && (
                                <p className="text-sm text-foreground mt-2 italic">{entry.notes}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {billingHistory.totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-border mt-6 pt-4">
                      <div className="text-sm text-muted-foreground">
                        Page {billingHistory.page} of {billingHistory.totalPages}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setHistoryPage((p) => p - 1)}
                          disabled={billingHistory.page === 1}
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setHistoryPage((p) => p + 1)}
                          disabled={billingHistory.page === billingHistory.totalPages}
                        >
                          Next
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Update Billing Dialog */}
      <Dialog open={updateBillingDialogOpen} onOpenChange={setUpdateBillingDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Update Billing</DialogTitle>
            <DialogDescription>
              Update the billing status and details for this project.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="billing-status">Billing Status</Label>
              <Select
                value={billingForm.billingStatus}
                onValueChange={(value) =>
                  setBillingForm((f) => ({ ...f, billingStatus: value as BillingStatus }))
                }
              >
                <SelectTrigger id="billing-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={BillingStatus.QUOTED}>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Quoted
                    </div>
                  </SelectItem>
                  <SelectItem value={BillingStatus.INVOICED}>
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Invoiced
                    </div>
                  </SelectItem>
                  <SelectItem value={BillingStatus.PAID}>
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Paid
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="billing-amount">Amount (optional)</Label>
                <Input
                  id="billing-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={billingForm.billingAmount}
                  onChange={(e) =>
                    setBillingForm((f) => ({ ...f, billingAmount: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing-currency">Currency</Label>
                <Select
                  value={billingForm.billingCurrency}
                  onValueChange={(value) =>
                    setBillingForm((f) => ({ ...f, billingCurrency: value }))
                  }
                >
                  <SelectTrigger id="billing-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="billing-reference">Invoice Reference (optional)</Label>
              <Input
                id="billing-reference"
                placeholder="INV-2024-001"
                value={billingForm.billingReference}
                onChange={(e) =>
                  setBillingForm((f) => ({ ...f, billingReference: e.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="billing-notes">Notes (optional)</Label>
              <Textarea
                id="billing-notes"
                placeholder="Add any billing notes..."
                value={billingForm.notes}
                onChange={(e) => setBillingForm((f) => ({ ...f, notes: e.target.value }))}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUpdateBillingDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitBilling}
              disabled={!billingForm.billingStatus || updateBillingStatus.isPending}
            >
              {updateBillingStatus.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        variant={confirmDialog.variant}
        loading={restoreProject.isPending}
      />
    </div>
  );
}
